import React, { useState } from 'react';
import { BaseConfigProps, commonStyles, conditionOperators } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const ConditionConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['simple', 'advanced']));
  const [mode, setMode] = useState<'simple' | 'advanced'>(node.data.config.mode || 'simple');
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const switchMode = (newMode: 'simple' | 'advanced') => {
    setMode(newMode);
    updateConfig('mode', newMode);
  };

  return (
    <div className="space-y-3">
      {/* Mode Selector */}
      <div>
        <label className={commonStyles.label}>
          Feltétel típusa
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'simple', label: 'Egyszerű feltétel', desc: 'Egy mező összehasonlítása', icon: '🎯' },
            { value: 'advanced', label: 'Haladó feltétel', desc: 'JavaScript kifejezés', icon: '⚡' }
          ].map((modeOption) => (
            <button
              key={modeOption.value}
              onClick={() => switchMode(modeOption.value as 'simple' | 'advanced')}
              className={`flex flex-col items-center justify-center p-3 rounded-md border text-sm transition-all min-h-[80px] ${
                mode === modeOption.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              <span className="text-2xl mb-1">{modeOption.icon}</span>
              <span className="font-medium text-xs text-center leading-tight">{modeOption.label}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">{modeOption.desc}</span>
              {mode === modeOption.value && (
                <span className="text-primary-500 mt-1">✓</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Simple Mode */}
      {mode === 'simple' && (
        <CollapsibleSection
          id="simple"
          title="Egyszerű Feltétel"
          icon="🎯"
          color="green"
          isExpanded={expandedSections.has('simple')}
          onToggle={toggleSection}
        >
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {/* Bal oldal */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Bal oldal
                </label>
                <input
                  type="text"
                  value={node.data.config.leftValue || ''}
                  onChange={(e) => updateConfig('leftValue', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{változó}} vagy érték"
                />
              </div>
              
              {/* Operátor */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Operátor
                </label>
                <select
                  value={node.data.config.operator || 'equals'}
                  onChange={(e) => updateConfig('operator', e.target.value)}
                  className={commonStyles.select}
                >
                  <option value="equals">Egyenlő (==)</option>
                  <option value="not_equals">Nem egyenlő (!=)</option>
                  <option value="greater_than">Nagyobb (&gt;)</option>
                  <option value="less_than">Kisebb (&lt;)</option>
                  <option value="greater_equal">Nagy/egyenlő (&gt;=)</option>
                  <option value="less_equal">Kis/egyenlő (&lt;=)</option>
                  <option value="contains">Tartalmazza</option>
                  <option value="not_contains">Nem tartalmazza</option>
                  <option value="starts_with">Ezzel kezdődik</option>
                  <option value="ends_with">Ezzel végződik</option>
                  <option value="is_empty">Üres</option>
                  <option value="is_not_empty">Nem üres</option>
                </select>
              </div>
              
              {/* Jobb oldal */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Jobb oldal
                </label>
                <input
                  type="text"
                  value={node.data.config.rightValue || ''}
                  onChange={(e) => updateConfig('rightValue', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{változó}} vagy érték"
                  disabled={node.data.config.operator === 'is_empty' || node.data.config.operator === 'is_not_empty'}
                />
              </div>
            </div>

            {/* Példák */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <div className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Példák:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li><code>{`{{email_sender}}`}</code> tartalmazza <code>@company.com</code></li>
                  <li><code>{`{{file_size_mb}}`}</code> nagyobb <code>5</code></li>
                  <li><code>{`{{customer_name}}`}</code> nem üres</li>
                </ul>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Advanced Mode */}
      {mode === 'advanced' && (
        <CollapsibleSection
          id="advanced"
          title="Haladó Feltétel"
          icon="⚡"
          color="orange"
          isExpanded={expandedSections.has('advanced')}
          onToggle={toggleSection}
        >
          <div className="space-y-3">
            <div>
              <label className={commonStyles.smallLabel}>
                JavaScript kifejezés
              </label>
              <div className={commonStyles.codeBlock}>
                <textarea
                  value={node.data.config.condition || ''}
                  onChange={(e) => updateConfig('condition', e.target.value)}
                  rows={4}
                  className="w-full bg-transparent text-green-400 font-mono text-sm resize-none focus:outline-none placeholder-gray-500"
                  placeholder="{{email_sender}}.includes('@vip.com') && {{file_size_mb}} > 10"
                />
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700">
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="text-gray-400">Syntax:</span>
                    <span className="text-blue-400">JavaScript</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Quick Operators */}
            <div>
              <label className={commonStyles.smallLabel}>
                Gyors operátorok
              </label>
              <div className="grid grid-cols-4 gap-1">
                {[
                  { op: ' === ', label: 'Egyenlő' },
                  { op: ' !== ', label: 'Nem egyenlő' },
                  { op: ' > ', label: 'Nagyobb' },
                  { op: ' < ', label: 'Kisebb' },
                  { op: ' >= ', label: 'Nagy/egyenlő' },
                  { op: ' <= ', label: 'Kis/egyenlő' },
                  { op: ' && ', label: 'És' },
                  { op: ' || ', label: 'Vagy' },
                  { op: '.includes(', label: 'Tartalmazza' },
                  { op: '.startsWith(', label: 'Kezdődik' },
                  { op: '.endsWith(', label: 'Végződik' },
                  { op: '.length', label: 'Hossz' }
                ].map((item) => (
                  <button
                    key={item.op}
                    onClick={() => {
                      const current = node.data.config.condition || '';
                      updateConfig('condition', current + item.op);
                    }}
                    className="px-2 py-1 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-orange-100 dark:hover:bg-orange-900/20 hover:text-orange-700 dark:hover:text-orange-300 transition-colors"
                    title={item.label}
                  >
                    {item.op.trim()}
                  </button>
                ))}
              </div>
            </div>

            {/* Variables Helper */}
            <div>
              <label className={commonStyles.smallLabel}>
                Gyakori változók
              </label>
              <div className="grid grid-cols-2 gap-1">
                {[
                  { var: '{{email_sender}}', desc: 'Email feladó' },
                  { var: '{{email_subject}}', desc: 'Email tárgy' },
                  { var: '{{file_name}}', desc: 'Fájl neve' },
                  { var: '{{file_size_mb}}', desc: 'Fájl méret' },
                  { var: '{{customer_name}}', desc: 'Ügyfél név' },
                  { var: '{{uploader_id}}', desc: 'Feltöltő ID' }
                ].map((item) => (
                  <button
                    key={item.var}
                    onClick={() => {
                      const current = node.data.config.condition || '';
                      updateConfig('condition', current + item.var);
                    }}
                    className="flex flex-col items-start text-left text-xs font-mono text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-2 py-1.5 rounded transition-colors"
                    title={item.desc}
                  >
                    <span className="font-medium">{item.var}</span>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">{item.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Preview */}
      <CollapsibleSection
        id="preview"
        title="Feltétel Előnézet"
        icon="👁️"
        color="gray"
        isExpanded={expandedSections.has('preview')}
        onToggle={toggleSection}
      >
        <div className={commonStyles.codeBlock}>
          <div className="text-green-400 font-mono text-xs">
            {mode === 'simple' ? (
              <div className="flex items-center space-x-2">
                <span className="text-blue-400">{node.data.config.leftValue || 'bal_oldal'}</span>
                <span className="text-red-400">{node.data.config.operator || 'equals'}</span>
                {node.data.config.operator !== 'is_empty' && node.data.config.operator !== 'is_not_empty' && (
                  <span className="text-green-400">"{node.data.config.rightValue || 'jobb_oldal'}"</span>
                )}
              </div>
            ) : (
              <div>
                {node.data.config.condition || 'Nincs feltétel megadva'}
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default ConditionConfig;